import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export function DescriptionPopoverButton({ text }: { text: string }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
    const btnRef = useRef<HTMLButtonElement | null>(null);
    const popRef = useRef<HTMLDivElement | null>(null);

    const place = () => {
        const el = btnRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const gap = 8;

        // anchor below button, align right edge
        let top = r.bottom + gap + window.scrollY - 60;
        let left = r.right + window.scrollX + 275;

        setPos({ top, left });
    };

    useLayoutEffect(() => {
        if (!open) return;
        place();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const onScroll = () => place();
        const onResize = () => place();

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };

        const onMouseDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (btnRef.current?.contains(target)) return;
            if (popRef.current?.contains(target)) return;
            setOpen(false);
        };

        window.addEventListener("scroll", onScroll, true);
        window.addEventListener("resize", onResize);
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("mousedown", onMouseDown);

        return () => {
            window.removeEventListener("scroll", onScroll, true);
            window.removeEventListener("resize", onResize);
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("mousedown", onMouseDown);
        };
    }, [open]);

    if (!text) return null;

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                style={helpBtn}
                aria-label="Show description"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen((v) => !v);
                }}
            >
                ?
            </button>

            {open && pos
                ? createPortal(
                    <div
                        ref={popRef}
                        style={{
                            ...popover,
                            top: pos.top,
                            left: pos.left,
                            transform: "translateX(-100%)", // right-align to button
                        }}
                        role="dialog"
                        aria-modal="false"
                    >
                        <div style={popoverHeader}>
                            <div style={popoverTitle}>Description</div>
                            <button
                                type="button"
                                style={closeBtn}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setOpen(false);
                                }}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>

                        <div style={popoverBody}>{text}</div>
                    </div>,
                    document.body
                )
                : null}
        </>
    );
}

// styles
const helpBtn: React.CSSProperties = {
    width: 18,
    height: 18,
    borderRadius: 999,
    border: "1px solid var(--fx-surface-borderstrong, #CBD5E1)",
    background: "var(--fx-surface-base, #FFFFFF)",
    color: "var(--fx-surface-textsecondary, #64748B)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 700,
    lineHeight: "18px",
    cursor: "pointer",
    userSelect: "none",
    flexShrink: 0,
};

const popover: React.CSSProperties = {
    position: "absolute",
    zIndex: 999999,
    width: 260,
    maxWidth: "calc(100vw - 24px)",
    borderRadius: 10,
    border: "1px solid var(--fx-surface-border, #E2E8F0)",
    background: "var(--fx-surface-base, #FFFFFF)",
    boxShadow: "0 14px 30px rgba(2, 6, 23, 0.18)",
    overflow: "hidden",
};

const popoverHeader: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 10px",
    borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)",
    background: "var(--fx-surface-sunken, #F8FAFC)",
};

const popoverTitle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 700,
    color: "var(--fx-surface-text, #0F172A)",
};

const closeBtn: React.CSSProperties = {
    width: 22,
    height: 22,
    borderRadius: 8,
    border: "1px solid var(--fx-surface-border, #E2E8F0)",
    background: "var(--fx-surface-base, #FFFFFF)",
    cursor: "pointer",
    lineHeight: "20px",
    fontSize: 16,
    color: "var(--fx-surface-textsecondary, #64748B)",
};

const popoverBody: React.CSSProperties = {
    padding: "10px 10px",
    fontSize: 12,
    color: "var(--fx-surface-text, #0F172A)",
    lineHeight: "16px",
    whiteSpace: "normal",
};
